// Escape with a menu open closes the menu alone (finance-real, 2026-10-22). It listens on window in the capture phase,
// ahead of the drawer's (Radix's) Escape on the document, and stops the key there, so the drawer the menu sits in stays
// open. The arrow keys, Home and End move between its items, and Enter on one clicks it. Escape and an item picked give
// focus back to the trigger, which says it opens a menu (aria-haspopup, aria-expanded, aria-controls on the role=menu
// list). An open menu is marked `data-dropdown-menu`, so an inline form's Escape (EscapeScope) leaves the key to it.
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useId, cloneElement, isValidElement } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils/cn";

/** A tag beside a label. It never shrinks, so a long label truncates and the tag stays whole. */
export function OptionBadge({ children }: { children: string }) {
  return (
    <span data-option-badge className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium leading-none text-gray-600">
      {children}
    </span>
  );
}

export interface DropdownMenuItem {
  id: string;
  label: string;
  /** A tag after the label, such as "External". */
  badge?: string;
  icon?: React.ReactNode;
  onClick: () => void;
  loading?: boolean;
  variant?: "default" | "danger";
  disabled?: boolean;
  separator?: never;
}

export interface DropdownMenuSeparator {
  id: string;
  separator: true;
}

export type DropdownMenuEntry = DropdownMenuItem | DropdownMenuSeparator;

interface DropdownMenuProps {
  items: DropdownMenuEntry[];
  trigger: React.ReactNode;
  width?: string;
  align?: "left" | "right";
  className?: string;
  menuClassName?: string;
  onOpenChange?: (open: boolean) => void;
  /** Render the open menu in a body-level portal with fixed positioning.
   * Opt in from inside overflow-hidden containers (modals), where the
   * default absolutely-positioned menu gets clipped. The menu closes on
   * scroll/resize since its fixed position would drift.
   * A menu inside a `Drawer` never portals, since the drawer is a modal dialog
   * and a menu outside it cannot be clicked (DD-051). */
  portal?: boolean;
  /** Long labels wrap instead of ellipsizing: for a menu sized to its longest item (`w-max`) in a drawer. */
  wrap?: boolean;
}

interface PortalMenuPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  minWidth: number;
  maxWidth: number;
}

/** How close a portal menu may come to the screen's side edges. */
const SCREEN_GAP = 8;

function isSeparator(
  entry: DropdownMenuEntry
): entry is DropdownMenuSeparator {
  return "separator" in entry && entry.separator === true;
}

/** The room a menu wants below its trigger before it opens up instead. */
const MENU_ROOM = 220;

/**
 * Which way a menu opens: up when the room below its trigger is under MENU_ROOM. Inside a drawer's body that room ends
 * at the body's edges, not the window's, since a drawer's menus never portal and its body clips them (DD-051). There
 * it opens up only when more room is above.
 */
function menuDirection(trigger: HTMLElement, rect: DOMRect): "down" | "up" {
  const body = trigger.closest("[data-drawer-body]");
  if (!body) return window.innerHeight - rect.bottom < MENU_ROOM ? "up" : "down";
  const edges = body.getBoundingClientRect();
  const below = Math.min(window.innerHeight, edges.bottom) - rect.bottom;
  const above = rect.top - Math.max(0, edges.top);
  return below < MENU_ROOM && above > below ? "up" : "down";
}

export function DropdownMenu({
  items,
  trigger,
  width = "w-44",
  align = "right",
  className,
  menuClassName,
  onOpenChange,
  portal = false,
  wrap = false,
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [direction, setDirection] = useState<"down" | "up">("down");
  const [portalPos, setPortalPos] = useState<PortalMenuPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Whether this open's portal menu has been measured: it is corrected at most once, so it cannot loop.
  const measuredRef = useRef(false);
  const menuId = useId();

  const updateOpen = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange]
  );

  /** Closes the menu and puts focus back on its trigger, as a menu button does. */
  const closeToTrigger = useCallback(() => {
    updateOpen(false);
    triggerRef.current?.querySelector<HTMLElement>("button, [href], input, [tabindex]")?.focus();
  }, [updateOpen]);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inContainer && !inMenu) {
        updateOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, updateOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      // The arrows, Home and End move between the open menu's items; Enter on one is its button's click.
      if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Home" || event.key === "End") {
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("button:not([disabled])") ?? []);
        if (items.length === 0) return;
        event.preventDefault();
        event.stopPropagation();
        const at = items.findIndex((b) => b === document.activeElement);
        const next =
          event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : event.key === "ArrowDown" ? (at + 1) % items.length : at <= 0 ? items.length - 1 : at - 1;
        items[next].focus();
        return;
      }
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      closeToTrigger();
    };
    window.addEventListener("keydown", closeOnEscape, true);
    return () => window.removeEventListener("keydown", closeOnEscape, true);
  }, [isOpen, closeToTrigger]);

  useEffect(() => {
    if (!isOpen || !portal) return;

    const closeOnPageScroll = (event: Event) => {
      // Scrolling inside the capped menu must not close it.
      if (menuRef.current?.contains(event.target as Node)) return;
      updateOpen(false);
    };
    const closeOnResize = () => updateOpen(false);
    window.addEventListener("scroll", closeOnPageScroll, true);
    window.addEventListener("resize", closeOnResize);
    return () => {
      window.removeEventListener("scroll", closeOnPageScroll, true);
      window.removeEventListener("resize", closeOnResize);
    };
  }, [isOpen, portal, updateOpen]);

  // A portal menu anchors to its trigger's side, so a trigger near a screen edge (a lone ⋮ wrapped to the left of a
  // phone's toolbar) would open it past that edge. Measured before the first paint, so the corrected place is what shows.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!isOpen || !portal || !portalPos || !menu || measuredRef.current) return;
    measuredRef.current = true;
    const box = menu.getBoundingClientRect();
    if (box.left < SCREEN_GAP) {
      setPortalPos({ ...portalPos, left: SCREEN_GAP, right: undefined });
    } else if (box.right > window.innerWidth - SCREEN_GAP) {
      setPortalPos({ ...portalPos, left: Math.max(SCREEN_GAP, window.innerWidth - SCREEN_GAP - box.width), right: undefined });
    }
  }, [isOpen, portal, portalPos]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const nextDirection = menuDirection(triggerRef.current, rect);
      setDirection(nextDirection);
      if (portal) {
        measuredRef.current = false;
        setPortalPos({
          minWidth: rect.width,
          maxWidth: window.innerWidth - 2 * SCREEN_GAP,
          ...(nextDirection === "down"
            ? { top: rect.bottom + 4 }
            : { bottom: window.innerHeight - rect.top + 4 }),
          ...(align === "right"
            ? { right: window.innerWidth - rect.right }
            : { left: rect.left }),
        });
      }
    }
    updateOpen(!isOpen);
  };

  const handleItemClick = (item: DropdownMenuItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.disabled && !item.loading) {
      // Focus goes back to the trigger first, so an item that moves focus on purpose (a form, a drawer) keeps it.
      closeToTrigger();
      item.onClick();
    }
  };

  const menu = isOpen && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          data-dropdown-menu=""
          className={cn(
            "bg-white rounded-md shadow-lg border border-gray-200 py-1 max-h-80 overflow-y-auto overflow-x-hidden",
            portal
              ? "fixed z-[60]"
              : cn(
                  "absolute z-50",
                  width,
                  align === "right" ? "right-0" : "left-0",
                  direction === "down" ? "mt-1" : "bottom-full mb-1"
                ),
            menuClassName
          )}
          style={portal && portalPos ? portalPos : undefined}
        >
          {items.map((entry) => {
            if (isSeparator(entry)) {
              return <hr key={entry.id} className="my-1" />;
            }

            return (
              <button
                key={entry.id}
                type="button"
                role="menuitem"
                onClick={(e) => handleItemClick(entry, e)}
                disabled={entry.disabled || entry.loading}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left",
                  "hover:bg-gray-50 focus:bg-gray-50 focus:outline-hidden",
                  entry.variant === "danger"
                    ? "text-red-600 hover:bg-red-50"
                    : "text-gray-700",
                  (entry.disabled || entry.loading) &&
                    "opacity-50 cursor-not-allowed"
                )}
              >
                {entry.icon && (
                  <span className="shrink-0 w-4 h-4">
                    {entry.loading ? (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                    ) : (
                      entry.icon
                    )}
                  </span>
                )}
                {/* Long labels (emails) ellipsize; the full text is the tooltip. */}
                <span className={cn("min-w-0", wrap ? "[overflow-wrap:anywhere]" : "truncate", !entry.badge && "flex-1")} title={entry.label}>
                  {entry.label}
                </span>
                {entry.badge && <OptionBadge>{entry.badge}</OptionBadge>}
              </button>
            );
          })}
        </div>
  );

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div ref={triggerRef} onClick={handleToggle}>
        {isValidElement<React.HTMLAttributes<HTMLElement>>(trigger)
          ? cloneElement(trigger, { "aria-haspopup": "menu", "aria-expanded": isOpen, "aria-controls": isOpen ? menuId : undefined })
          : trigger}
      </div>

      {portal ? menu && createPortal(menu, document.body) : menu}
    </div>
  );
}
